import { Box, Typography, Button, Paper, Alert, Stack } from "@mui/material";
import { Refresh, LocationOff } from "@mui/icons-material";
import React from "react";
import { logger } from "../../utils/logger";

class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    logger.error("Map Error Boundary caught an error:", error);
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `map_error_${Date.now()}_${Math.random()
      .toString(36)
      .substr(2, 9)}`;

    logger.error("Map Error Boundary caught an error:", error, errorInfo);

    this.setState({
      error,
      errorInfo,
      errorId,
    });

    // Track map-specific error metrics
    if (import.meta.env.MODE === "production") {
      // Send to error monitoring with map context
      logger.error("Map Error:", {
        error: error.toString(),
        errorInfo,
        errorId,
        mapType: this.props.mapType,
        deviceId: this.props.deviceId,
        component: "Map",
      });
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  handleRefreshPage = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorId } = this.state;
      const { mapType, isWidget, fallbackHeight = "400px" } = this.props;
      const isDevelopment = import.meta.env.MODE === "development";

      return (
        <Box
          sx={{
            height: fallbackHeight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: isWidget ? 2 : 3,
            backgroundColor: "rgba(0, 0, 0, 0.02)",
            borderRadius: isWidget ? 1 : 2,
          }}
        >
          <Paper
            elevation={isWidget ? 1 : 2}
            sx={{
              maxWidth: isWidget ? 300 : 500,
              width: "100%",
              p: isWidget ? 2 : 3,
              textAlign: "center",
            }}
          >
            <Stack spacing={isWidget ? 1.5 : 2} alignItems="center">
              <LocationOff
                sx={{
                  fontSize: isWidget ? 40 : 56,
                  color: "error.main",
                }}
              />

              <Typography
                variant={isWidget ? "subtitle1" : "h6"}
                component="h1"
                gutterBottom
              >
                Map Loading Error
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: isWidget ? "0.875rem" : "1rem" }}
              >
                {mapType === "widget"
                  ? "Map widget failed to load"
                  : mapType === "device"
                    ? "Device location map failed to load"
                    : "Map failed to load properly"}
              </Typography>

              {isDevelopment && (
                <Alert
                  severity="warning"
                  sx={{ width: "100%", textAlign: "left", fontSize: "0.75rem" }}
                >
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    sx={{ fontSize: "0.75rem" }}
                  >
                    Map Error Details (Development Mode):
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ fontSize: "0.625rem", mb: 1 }}
                  >
                    Map Type: {mapType || "Unknown"}
                  </Typography>
                  <Typography
                    variant="body2"
                    component="pre"
                    sx={{ fontSize: "0.625rem", mb: 1 }}
                  >
                    {error && error.toString()}
                  </Typography>
                  {errorInfo && (
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontSize: "0.625rem",
                        maxHeight: isWidget ? 60 : 100,
                        overflow: "auto",
                      }}
                    >
                      {errorInfo.componentStack}
                    </Typography>
                  )}
                </Alert>
              )}

              {errorId && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: "0.7rem" }}
                >
                  Error ID: {errorId}
                </Typography>
              )}

              <Stack direction={isWidget ? "column" : "row"} spacing={1}>
                <Button
                  variant="contained"
                  onClick={this.handleRetry}
                  startIcon={<Refresh />}
                  size={isWidget ? "small" : "medium"}
                >
                  Retry Map
                </Button>

                {!isWidget && (
                  <Button
                    variant="outlined"
                    onClick={this.handleRefreshPage}
                    size="medium"
                  >
                    Refresh Page
                  </Button>
                )}
              </Stack>

              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem" }}
              >
                {isWidget
                  ? "Check your network connection"
                  : "This might be a network or Mapbox API issue"}
              </Typography>
            </Stack>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Higher-order component for easier usage with map components
export const withMapErrorBoundary = (Component, options = {}) => {
  return function MapErrorBoundaryWrapper(props) {
    return (
      <MapErrorBoundary
        mapType={props.mapType || "general"}
        deviceId={props.deviceId || props.device?.id}
        isWidget={props.isWidget || false}
        fallbackHeight={props.fallbackHeight ?? options.fallbackHeight}
      >
        <Component {...props} />
      </MapErrorBoundary>
    );
  };
};

// Specialized HOCs for different map types
export const withDeviceMapErrorBoundary = (Component) => {
  return function DeviceMapErrorBoundaryWrapper(props) {
    return (
      <MapErrorBoundary
        mapType="device"
        deviceId={props.deviceId || props.device?.id}
        fallbackHeight="100%"
      >
        <Component {...props} />
      </MapErrorBoundary>
    );
  };
};
